import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { AGURenderService } from '../../app-surface/render.service';
import {
    SplitCircleRow,
    SplitInListJobSub,
    SplitPackedSub,
    SplitRailSpotSub,
    type AGUAdCircleRow,
    type AGUAdJobParts,
    type AGUAdKind,
    type AGUAdPlacement,
    type AGUAdRailParts,
} from '../../ads/ad-placement';
import { AGUAdSessionService } from '../../ads/ad-session.service';
import { RISE_EASING, ReducedMotion } from '../../motion/rise';

/** The dismiss collapse: 260ms on the prototype's standard curve (P/Member_Hub.html:1371). */
export const AD_DISMISS_MS = 260;
export const AD_DISMISS_EASING = 'cubic-bezier(.4,0,.2,1)';
/** A landing strip stepping back in once results give way: 260ms, 6px rise (P/Member_Hub.html:1399). */
export const AD_REVEAL_MS = 260;

/** The kinds that carry the 56px logo (`.fx-logo.lg`, P/Member_Hub.html:1282, 1506, 1537, 1551). */
const LARGE_LOGO_KINDS: readonly AGUAdKind[] = ['feature', 'in-list-job', 'rail-spot'];
/** The kinds the prototype gives a ✕ (`data-fx-dismiss`); Circles and the no-results line have none (P/Member_Hub.html:1592-1644). */
const DISMISSIBLE_KINDS: readonly AGUAdKind[] = ['leaderboard', 'sidebar', 'feature', 'strip', 'rail-spot', 'in-list-job'];
/** The kinds whose CTA carries the 12px arrow (`ARROW`, P/Member_Hub.html:1274, 1388, 1511, 1541, 1596). */
const ARROW_KINDS: readonly AGUAdKind[] = ['feature', 'strip', 'rail-spot', 'circles'];
/** The Circle cards' button label, prototype chrome rather than data (P/Member_Hub.html:1593); detail rows take the creative's `cta`. */
const JOIN_LABEL = 'Join';
/** A pressed Join (P/Member_Hub.html:1376). */
const JOINED_LABEL = 'Joined ✓';

/**
 * One Featured placement (PD §3; P/Member_Hub.html:1268-1645), rendered by `Placement.kind`: the display leaderboard and
 * sidebar rectangle, the Home feature, the finder hero strip, the Jobs rail spotlight and in-list job card, the People
 * Circles row and no-results line, and the person-detail Circles. Copy is the placement's data; this component only lays
 * it out.
 *
 * The host element IS the slot (it carries `data-kind` and the slot's margins), so the dismiss collapse animates the host the
 * way the prototype animates its `[data-fx]` root. On finish the id goes to the surface's `AGUAdSessionService` — the slot's
 * own `Select` then leaves it out — and `Dismissed` emits it. A CTA with no `href` (every creative this round) renders as a
 * non-link span. "Why this?" is not built (PD §3; the prototype disables it, P/Member_Hub.html:1288, 1380).
 */
@Component({
    standalone: false,
    selector: 'agu-ad-slot',
    templateUrl: './ad-slot.component.html',
    styleUrl: './ad-slot.component.scss',
    host: {
        class: 'agu-ad',
        role: 'complementary',
        '[attr.data-kind]': 'Placement.kind',
        '[attr.data-house]': "Placement.house ? 'true' : null",
        '[attr.aria-label]': 'AriaLabel',
    },
})
export class AGUAdSlotComponent implements OnInit, OnDestroy {
    @Input({ required: true }) Placement!: AGUAdPlacement;
    /** True when the slot is coming back into view (a finder strip after results), which fades it in (P/Member_Hub.html:1399). */
    @Input() Reveal = false;
    /** The dismissed placement's id, after the collapse. */
    @Output() Dismissed = new EventEmitter<string>();

    private animation: Animation | null = null;
    /**
     * The Circle cards/rows pressed (P/Member_Hub.html:1375-1376), keyed `<placement id>#<index>`: a host that swaps the bound
     * creative in place (person detail moving between people) starts the new one unpressed, and swapping back to a creative
     * finds its presses again — they last for the life of this slot, the surface session.
     */
    private readonly joined = new Set<string>();
    private dismissing = false;

    constructor(
        private readonly host: ElementRef<HTMLElement>,
        @Optional() @Inject(AGUAdSessionService) private readonly session: AGUAdSessionService | null,
        @Optional() @Inject(AGURenderService) private readonly render: AGURenderService | null
    ) {}

    ngOnInit(): void {
        const el = this.host.nativeElement;
        if (!this.Reveal || ReducedMotion() || typeof el.animate !== 'function') return;
        this.animation = el.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: AD_REVEAL_MS, easing: RISE_EASING });
    }

    ngOnDestroy(): void {
        this.animation?.cancel();
    }

    /** "Featured" for a paid creative, "From AGU" for a house one (P/Member_Hub.html:1277-1278, 1363). */
    get TagText(): string {
        return this.Placement.house ? 'From AGU' : 'Featured';
    }

    /** The display ads' bar label (P/Member_Hub.html:1441, 1484). */
    get BarLabel(): string {
        return this.Placement.house ? 'From AGU' : 'Advertisement';
    }

    get AriaLabel(): string {
        if (this.Placement.kind === 'leaderboard' || this.Placement.kind === 'sidebar') return 'Advertisement';
        if (this.Placement.kind === 'circles' || this.Placement.kind === 'detail-circles') return 'Circles';
        return this.TagText;
    }

    get Large(): boolean {
        return LARGE_LOGO_KINDS.includes(this.Placement.kind);
    }

    get Dismissible(): boolean {
        return DISMISSIBLE_KINDS.includes(this.Placement.kind);
    }

    get HasArrow(): boolean {
        return ARROW_KINDS.includes(this.Placement.kind);
    }

    /** The sponsor mark's plain first word: "TERRA" of "TERRA ANALYTICS" (P/Member_Hub.html:1460, `TERRA<b>ANALYTICS</b>`). */
    get MarkFirst(): string {
        return (this.Placement.logoText ?? '').split(' ')[0] ?? '';
    }

    /** The mark's remaining words, set bold in the sponsor's accent. */
    get MarkRest(): string {
        return (this.Placement.logoText ?? '').split(' ').slice(1).join(' ');
    }

    /** `circles` / `detail-circles`: the packed lede (the `sub`'s first `|` row). */
    get Lede(): string {
        return SplitPackedSub(this.Placement.sub)[0] ?? '';
    }

    /** `circles` / `detail-circles`: one row per packed Circle after the lede. */
    get Circles(): AGUAdCircleRow[] {
        return SplitPackedSub(this.Placement.sub).slice(1).map(SplitCircleRow);
    }

    get Job(): AGUAdJobParts {
        return SplitInListJobSub(this.Placement.sub);
    }

    get Rail(): AGUAdRailParts {
        return SplitRailSpotSub(this.Placement.sub);
    }

    /** The house sidebar's "Eos" wordmark beside the AGU mark — prototype chrome (P/Member_Hub.html:1420, 1433). */
    get ShowEos(): boolean {
        return this.Placement.kind === 'sidebar' && this.Placement.house && this.Placement.logoIsAgu;
    }

    private JoinKey(index: number): string {
        return `${this.Placement.id}#${index}`;
    }

    IsJoined(index: number): boolean {
        return this.joined.has(this.JoinKey(index));
    }

    /** "Join" (the detail rows' `cta`), or "Joined ✓" once pressed. */
    JoinLabel(index: number): string {
        if (this.IsJoined(index)) return JOINED_LABEL;
        return this.Placement.kind === 'detail-circles' && this.Placement.cta ? this.Placement.cta : JOIN_LABEL;
    }

    /** Presses or releases a Circle's Join (P/Member_Hub.html:1375-1376). */
    ToggleJoin(index: number): void {
        const key = this.JoinKey(index);
        if (this.joined.has(key)) this.joined.delete(key);
        else this.joined.add(key);
    }

    /** ✕: collapse height, opacity, margins, padding and border to 0 over 260ms, instantly under reduced motion (P/Member_Hub.html:1366-1374). */
    Dismiss(): void {
        if (this.dismissing) return;
        this.dismissing = true;
        const el = this.host.nativeElement;
        if (ReducedMotion() || typeof el.animate !== 'function') {
            this.Finish();
            return;
        }
        el.style.overflow = 'hidden';
        this.animation = el.animate(
            [
                { opacity: 1, height: `${el.offsetHeight}px` },
                { opacity: 0, height: '0px', marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px', borderWidth: '0px' },
            ],
            // Held at the collapsed frame until the host's @if removes the slot.
            { duration: AD_DISMISS_MS, easing: AD_DISMISS_EASING, fill: 'forwards' }
        );
        this.animation.onfinish = () => this.Finish();
    }

    private Finish(): void {
        this.session?.Dismiss(this.Placement.id);
        this.Dismissed.emit(this.Placement.id);
        // The animation's finish arrives outside any Angular notification on this zoneless host.
        this.render?.Notify();
    }
}
